import qs from 'qs';
import { differenceInCalendarDays } from 'date-fns';
import { DateHelper } from 'pureUi/DatePicker';
import { useTypedQueryParams } from 'hooks/useTypedQueryParams';
import { IBookingBuilderQueryParams, IParsedBookingBuilderParams, ILodging } from './types';

/**
 * Safely parse lodgings array from query parameters
 * Handles the complex lodgings[0][field] structure
 */
function parseLodgings(queryObj: any): ILodging[] {
  const lodgings: ILodging[] = [];

  if (queryObj.lodgings && Array.isArray(queryObj.lodgings)) {
    queryObj.lodgings.forEach((lodging: any) => {
      const parsedLodging: ILodging = {};

      // numberOfAdults - parse as number, default to 2
      parsedLodging.numberOfAdults = lodging.numberOfAdults ? parseInt(lodging.numberOfAdults, 10) : 2;

      // agesOfAllChildren - ensure it's always an array
      if (lodging.agesOfAllChildren) {
        if (Array.isArray(lodging.agesOfAllChildren)) {
          parsedLodging.agesOfAllChildren = lodging.agesOfAllChildren.map(age => parseInt(age, 10));
        } else if (typeof lodging.agesOfAllChildren === 'string' && lodging.agesOfAllChildren.trim()) {
          // Handle comma-separated string like "2,5" or single value "2"
          parsedLodging.agesOfAllChildren = lodging.agesOfAllChildren
            .split(',')
            .map(age => parseInt(age.trim(), 10))
            .filter(age => !isNaN(age));
        } else {
          parsedLodging.agesOfAllChildren = [];
        }
      } else {
        parsedLodging.agesOfAllChildren = []; // Always ensure this is an array
      }

      if (lodging.repeatCustomerStatus) {
        parsedLodging.repeatCustomerStatus = lodging.repeatCustomerStatus;
      }

      lodgings.push(parsedLodging);
    });
  }

  return lodgings;
}

/**
 * Parse meal plan categories array
 */
function parseMealPlanCategories(queryObj: any): string[] {
  if (queryObj.mealPlanCategories && Array.isArray(queryObj.mealPlanCategories)) {
    return queryObj.mealPlanCategories;
  }
  return [];
}

/**
 * Resolve the travel agent UUID from URL parameters
 * Uses tauuid as the standard parameter name, with fallback to taUuid for backward compatibility
 */
function resolveTravelAgentUuid(queryObj: any): string | undefined {
  return queryObj.tauuid || queryObj.taUuid;
}

/**
 * Calculate selected dates array from start/end dates
 */
function calculateSelectedDates(startDate?: string, endDate?: string): string[] {
  if (!startDate || !endDate) return [];

  try {
    const length = differenceInCalendarDays(new Date(endDate), new Date(startDate)) + 2;
    const firstTimeStamp = new Date(startDate).getTime();

    return DateHelper.generateDatesFrom(firstTimeStamp, length, 'en-US')
      .map(dateObject => dateObject.dateString)
      .map(date => date.split('T')[0]);
  } catch (error) {
    console.warn('Failed to calculate selected dates:', error);
    return [];
  }
}

/**
 * Calculate total nights between dates
 */
function calculateTotalNights(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 0;

  try {
    return differenceInCalendarDays(new Date(endDate), new Date(startDate));
  } catch (error) {
    console.warn('Failed to calculate total nights:', error);
    return 0;
  }
}

/**
 * Transform raw query object into booking builder parameters with proper parsing
 * Used internally by parseBookingBuilderQueryParams and transformBookingBuilderParams
 */
function parseRawQueryToBookingBuilderParams(queryObj: any): IBookingBuilderQueryParams {
  return {
    startDate: queryObj.startDate as string,
    endDate: queryObj.endDate as string,
    clientCountryCode: queryObj.clientCountryCode as string,
    companyUuid: queryObj.companyUuid as string,
    hotelUuid: queryObj.hotelUuid as string,
    tauuid: queryObj.tauuid as string,
    membership: queryObj.membership as string,
    lodgings: parseLodgings(queryObj),
    mealPlanCategories: parseMealPlanCategories(queryObj),

    // Capture any other unexpected parameters
    ...Object.keys(queryObj).reduce((acc, key) => {
      if (
        ![
          'startDate',
          'endDate',
          'clientCountryCode',
          'companyUuid',
          'hotelUuid',
          'tauuid',
          'membership',
          'lodgings',
          'mealPlanCategories',
        ].includes(key)
      ) {
        acc[key] = queryObj[key];
      }
      return acc;
    }, {} as Record<string, any>),
  };
}

/**
 * Extract and parse booking builder query parameters from URL
 * Handles all the chaos and returns a clean, typed object
 */
export function parseBookingBuilderQueryParams(search?: string): IParsedBookingBuilderParams {
  const queryString = search || window.location.search;
  const cleanQueryString = queryString.replace('?', '');

  if (!cleanQueryString) {
    return {};
  }

  try {
    const queryObj = qs.parse(cleanQueryString);
    const params = parseRawQueryToBookingBuilderParams(queryObj);
    return transformBookingBuilderParams(params);
  } catch (error) {
    console.error('Failed to parse booking builder query params:', error);
    return {};
  }
}

/**
 * Transform function to add computed values to booking builder query parameters
 * Used with useTypedQueryParams hook
 */
export function transformBookingBuilderParams(rawQueryObj: any): IParsedBookingBuilderParams {
  // First parse the raw query object into properly typed booking builder params
  const params = parseRawQueryToBookingBuilderParams(rawQueryObj);

  // Then add computed values
  return {
    ...params,
    selectedTravelAgentUuid: resolveTravelAgentUuid(rawQueryObj),
    selectedMembershipUuid: params.membership,
    selectedHotelUuid: params.hotelUuid,
    selectedCompanyUuid: params.companyUuid,
    totalNights: calculateTotalNights(params.startDate, params.endDate),
    selectedDates: calculateSelectedDates(params.startDate, params.endDate),
  };
}

/**
 * Hook to get parsed booking builder query parameters
 * Re-parses when location changes
 */
export function useBookingBuilderQueryParams(): IParsedBookingBuilderParams {
  return parseBookingBuilderQueryParams();
}

/**
 * Validate booking builder query parameters
 * Returns validation result with errors if invalid
 */
export interface IValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateBookingBuilderParams(params: IParsedBookingBuilderParams): IValidationResult {
  const errors: string[] = [];

  // Check if we have any parameters at all
  const hasAnyParams = Object.keys(params).length > 0;
  if (!hasAnyParams) {
    errors.push('No query parameters found');
    return { isValid: false, errors };
  }

  // Validate startDate
  if (!params.startDate) {
    errors.push('Missing required parameter: startDate');
  }

  // Validate endDate
  if (!params.endDate) {
    errors.push('Missing required parameter: endDate');
  }

  // Validate lodgings
  if (!params.lodgings || !Array.isArray(params.lodgings) || params.lodgings.length === 0) {
    errors.push('Missing or empty lodgings array');
  } else {
    params.lodgings.forEach((lodging, index) => {
      if (!lodging.numberOfAdults || lodging.numberOfAdults < 1 || lodging.numberOfAdults > 10) {
        errors.push(`Invalid numberOfAdults in lodging ${index + 1}: must be between 1 and 10`);
      }

      if (lodging.agesOfAllChildren && !Array.isArray(lodging.agesOfAllChildren)) {
        errors.push(`Invalid agesOfAllChildren in lodging ${index + 1}: must be an array`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Helper function to validate date string format (YYYY-MM-DD or ISO datetime)
 */
function isValidDateString(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  // Check format using regex - accept both date-only and ISO datetime formats
  const dateOnlyRegex = /^\d{4}-\d{2}-\d{2}$/;
  const isoDatetimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;

  if (!dateOnlyRegex.test(dateString) && !isoDatetimeRegex.test(dateString)) {
    return false;
  }

  // Check if it's a valid date
  const date = new Date(dateString);
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false;
  }

  // For date-only strings, check if ISO starts with the date
  if (dateOnlyRegex.test(dateString)) {
    return date.toISOString().startsWith(dateString);
  }

  // For ISO datetime strings, just ensure it parsed correctly
  return true;
}

/**
 * Hook to get parsed booking builder query parameters using useTypedQueryParams
 * Provides reactive updates when URL changes and proper typing
 */
export function useParsedBookingBuilderQueryParams(): IParsedBookingBuilderParams {
  return useTypedQueryParams<any, IParsedBookingBuilderParams>({
    transform: transformBookingBuilderParams,
  });
}
